/**
 * PillTabs — canonical pill-shaped tab navigation.
 *
 * Mockup parity: the mockup uses a single tab idiom across every
 * surface — pill-shaped buttons, active = primary fill, inactive =
 * card border (`bg-card border border-border text-muted-foreground`).
 * Pre-(m.6) we had three places already authoring this shape
 * (Envoy view-mode toggle, Vault tab nav, settings underline tabs
 * was the wrong-shape outlier). `<PillTabs>` is the single way to
 * express it.
 *
 * Renders a WAI-ARIA `tablist` with `tab` children. Consumers wire
 * the active panel content separately — this primitive is purely
 * the tab strip.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1
 * Requirements: 14.3, 14.4, 18.5
 */

import type { JSX } from 'react';
import { cn } from '@/lib/utils';

export interface PillTabsItem<TId extends string = string> {
  id: TId;
  label: string;
  /** Optional badge text rendered to the right of the label
   *  (e.g. unread count, pending count). */
  badge?: string | number;
}

export interface PillTabsProps<TId extends string = string> {
  /** Tab definitions in display order. The first is the default. */
  items: ReadonlyArray<PillTabsItem<TId>>;
  /** Currently-active tab id. */
  activeId: TId;
  /** Notified whenever a tab is selected. */
  onChange: (id: TId) => void;
  /** Accessible group label. Read by screen readers. */
  ariaLabel: string;
  /** Optional className passthrough. */
  className?: string;
}

export function PillTabs<TId extends string = string>({
  items,
  activeId,
  onChange,
  ariaLabel,
  className,
}: PillTabsProps<TId>): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-testid="pill-tabs"
      className={cn('inline-flex flex-wrap gap-2', className)}
    >
      {items.map((item) => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`pill-tab-${item.id}`}
            aria-selected={isActive}
            aria-controls={`pill-tab-panel-${item.id}`}
            tabIndex={isActive ? 0 : -1}
            data-testid={`pill-tab-${item.id}`}
            onClick={() => onChange(item.id)}
            className={cn(
              'pill-shape touch-target gap-2 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              isActive
                ? 'bg-primary text-primary-foreground shadow-subtle'
                : 'bg-card border border-border text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <span>{item.label}</span>
            {item.badge !== undefined && item.badge !== null ? (
              <span
                aria-hidden="true"
                className={cn(
                  'inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-semibold',
                  isActive
                    ? 'bg-primary-foreground/15 text-primary-foreground'
                    : 'bg-muted text-foreground',
                )}
              >
                {item.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default PillTabs;
